/* Notification Center — modal dialogs ported 1:1 from the standalone HTML
 * prototype (notification-center.html): the 6-step New Rule wizard
 * (Source · Trigger · Target · Severity · Recipients · Review) and the New/Edit
 * modals for Categories (icon picker), Recipient Groups (member rows) and Email
 * Templates (subject + body + token chips).  Client-only (demo module) — each
 * dialog builds a typed object and hands it to the store.
 *
 * TRIGGER step (2026-09-19): step 2 chooses WHICH workflow form/event evaluates
 * and fires the rule.  Options come from Default Settings (triggerForms.ts) —
 * only the forms enabled there are offered; the choice is saved on the rule. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { StepIsland } from '../../components/StepIsland';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { groupRefsFor, type NcCategory, type NcRule, type NcRecipient, type NcRecipientGroup, type NcGroupUser, groupMembersDisplay, groupChannels, type NcTemplate, type Severity, type Purpose } from './data';
import { buildPermitTargets, type PlPermit } from './permitLibrary';
import { getHoldTimeCatalog, getPermitLibrary } from './api';
import { PermitTargetsGrid } from './PermitTargetsGrid';
import { HoldTimeTargetsGrid } from './HoldTimeTargetsGrid';
import { holdTimeTargetsError, htKey, isHoldTimeCategory, targetLabel, toSavedTargets, type HoldTimeCatalogRow, type HoldTimeSel } from './holdTime';
import { RecipientsEditor } from './RecipientsEditor';
import { GroupUsersEditor } from './GroupUsersEditor';
import { RecipientGroupPicker } from './RecipientGroupPicker';
import { EmailTemplatePicker } from './EmailTemplatePicker';
import { RichTextEditor } from '../settings/systemManagement/RichTextEditor';
import { enabledTriggerForms, triggerLabels, triggersEvaluationText } from './triggerForms';
import { useNcStore } from './store';
import { TemplateFieldsManager, type FieldCategory } from './TemplateFieldsManager';
import { checkTemplateFields, listTemplateFields, previewTemplate, templateFieldRefusal, type TemplateField, type TemplateFieldWarning, type TemplatePreview } from './templateFieldsApi';

/* category → default purpose (HTML RULE_PURPOSE map) */
const CAT_PURPOSE: Record<string, Purpose> = {
  'Result Exceedance': 'violation', 'Sample Receipt': 'reminder', 'Sample Receipt & Condition': 'reminder',
  'Compliance & TAT': 'violation', 'QC & Workflow': 'reminder', 'Client & Sales': 'internal',
  'Subcontract & Logistics': 'reminder', 'System & Data': 'system', 'Capacity & Trend': 'internal',
};

/* ── shared modal shell ── */
export function Modal({ title, onClose, width = 520, disableBackdropClose = false, children }: {
  title: string; onClose: () => void; width?: number;
  /** When true, clicking the backdrop does NOT close — the popup closes only via
   *  the X, the footer Cancel, or a successful save (e.g. the New Rule wizard). */
  disableBackdropClose?: boolean; children: ReactNode;
}) {
  return (
    <div className="nc-overlay" onClick={disableBackdropClose ? undefined : (e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="nc-modal" style={{ width }}>
        <div className="nc-modal-hd"><h2>{title}</h2><button className="nc-modal-x" onClick={onClose} aria-label="Close"><i className="ti ti-x" /></button></div>
        {children}
      </div>
    </div>
  );
}
function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <span className={`nc-toggle${on ? ' on' : ''}`} role="button" onClick={onClick} />;
}

/* ════════════════════ New Rule wizard (5 steps) ════════════════════ */
/** A rule source. `category` (optional) is the NAME of the Rule Category picked
 *  automatically when the user selects this source; it is matched against the
 *  categories loaded from the database, so the saved CategoryId is always the
 *  live row's — no database id is written here. A source without one leaves the
 *  Category dropdown as it is. */
interface WatchSource { icon: string; label: string; sub: string; category?: string }
const WATCH: WatchSource[] = [
  { icon: 'ti-droplet', label: 'Analysis Result', sub: 'Result vs limit/value', category: 'Result Exceedance' },
  { icon: 'ti-clock-hour-4', label: 'Date / Age', sub: 'TAT, calibration, contracts', category: 'Hold Time' },
  { icon: 'ti-flask', label: 'QC Batch', sub: 'QC pass/fail, batch age' },
  { icon: 'ti-users', label: 'Client Activity', sub: 'Cadence, contribution' },
  { icon: 'ti-server-2', label: 'System Event', sub: 'Import, integration, formula' },
  { icon: 'ti-chart-line', label: 'Aggregate Trend', sub: 'Backlog, capacity, volume' },
];
const SEV: { key: Severity; label: string; sub: string }[] = [
  { key: 'info', label: 'Info', sub: 'In-app only, batched into daily digest' },
  { key: 'warning', label: 'Warning', sub: 'In-app now + included in digest email' },
  { key: 'critical', label: 'Critical', sub: 'In-app + standalone email immediately' },
];
// The Trigger step is shown ONLY for Analysis Result / Analyst rules (WATCH[0]).
// Every other source keeps its flow without a Trigger step. Both flows pick the
// rule's Email Template just before Review. The step numbering is derived from
// whichever list is active, so it adjusts itself.
const STEP_LABELS_TRIGGER = ['Source', 'Trigger', 'Target', 'Severity', 'Recipients', 'Email Template', 'Review'];
const STEP_LABELS_PLAIN = ['Source', 'Target', 'Severity', 'Recipients', 'Email Template', 'Review'];
/** WATCH index whose source enables the Trigger step (Analysis Result). */
const TRIGGER_WATCH_INDEX = 0;
/** The source the wizard opens on. */
const INITIAL_WATCH_INDEX = TRIGGER_WATCH_INDEX;

/** The ACTIVE category a source auto-selects, by name (case- and space-
 *  insensitive), or undefined when the source has none or that category is not
 *  an active row — the dropdown is then left as it is. */
function categoryForSource(source: WatchSource | undefined, active: NcCategory[]): NcCategory | undefined {
  const want = source?.category?.trim().toLowerCase();
  if (!want) return undefined;
  return active.find((c) => c.name.trim().toLowerCase() === want);
}

export function NewRuleWizard({ categories, defaultCategory, onClose, onCreate }: {
  categories: NcCategory[]; defaultCategory?: string;
  onClose: () => void; onCreate: (categoryName: string, rule: NcRule) => void;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [watch, setWatch] = useState(INITIAL_WATCH_INDEX);
  // Trigger — the workflow forms enabled in Default Settings are the selectable
  // rows; each has its own ON/OFF toggle and several may be ON at once. Default
  // the first row ON so "at least one before Next" holds out of the box.
  const triggerOpts = enabledTriggerForms();
  const [triggers, setTriggers] = useState<Record<string, boolean>>(
    () => Object.fromEntries(triggerOpts.map((f, i) => [f.id, i === 0])));
  const selectedTriggers = triggerOpts.filter((f) => triggers[f.id]).map((f) => f.id);
  // A NEW rule may only be filed under an ACTIVE Rule Category (the combo lists
  // just those). A disabled category is never offered here; existing rules keep
  // whatever category they were saved under — this only gates NEW selection.
  const activeCategories = categories.filter((c) => c.active);
  // Opened from a category's own page → keep that category. Otherwise start on
  // the category the opening source auto-selects, then the first active one.
  const initialCategory = (defaultCategory && activeCategories.some((c) => c.name === defaultCategory))
    ? defaultCategory
    : (categoryForSource(WATCH[INITIAL_WATCH_INDEX], activeCategories)?.name
      ?? activeCategories[0]?.name ?? defaultCategory ?? categories[0]?.name ?? 'Result Exceedance');
  const [category, setCategory] = useState(initialCategory);
  // Selecting a DIFFERENT source auto-selects its category (Analysis Result →
  // Result Exceedance, Date / Age → Hold Time). Re-clicking the selected source
  // does nothing, so a category the user picked by hand is not overwritten by
  // accident; the dropdown stays free to change afterwards.
  function selectSource(i: number) {
    if (i === watch) return;
    setWatch(i);
    const auto = categoryForSource(WATCH[i], activeCategories);
    if (auto) setCategory(auto.name);
  }
  // Target — the SAME Permit Library grid as Rule Detail, fed from the FULL live
  // Permit Library (the API, source of truth) so EVERY configured parameter is
  // listed. NEW rules start with every parameter OFF; the user turns on only the
  // ones to monitor. State lives here so the selection survives Back/Next.
  const [library, setLibrary] = useState<PlPermit[] | null>(null);
  const targetsLoading = library === null;
  useEffect(() => {
    let alive = true;
    getPermitLibrary().then((l) => { if (alive) setLibrary(l); }).catch(() => { if (alive) setLibrary([]); });
    return () => { alive = false; };
  }, []);
  const permitTargets = useMemo(() => buildPermitTargets(library ?? []), [library]);
  const [tgtEnabled, setTgtEnabled] = useState<Record<string, boolean>>({});
  // Seed every parameter OFF once the library loads (new rule = nothing selected).
  useEffect(() => {
    if (library === null) return;
    setTgtEnabled(Object.fromEntries(permitTargets.map((t) => [t.key, false])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [library]);
  const [tgtExpanded, setTgtExpanded] = useState<Record<string, boolean>>({});
  const enabledTargets = permitTargets.filter((t) => tgtEnabled[t.key] ?? false);
  // HOLD TIME category (RTCFR 2026-10-14) — its Target step is the Test Method
  // grid (Matrix · Test · Method · Hold Time · Alert · Warn Before), not the Permit
  // Library. The catalog loads the first time Hold Time is chosen; the selection
  // lives here so it survives Back/Next and a category switch back and forth.
  const isHoldTime = isHoldTimeCategory(category);
  const [htCatalog, setHtCatalog] = useState<HoldTimeCatalogRow[] | null>(null);
  const [htFailed, setHtFailed] = useState(false);
  useEffect(() => {
    if (!isHoldTime || htCatalog !== null) return;
    let alive = true;
    getHoldTimeCatalog()
      .then((rows) => { if (alive) { setHtCatalog(rows); setHtFailed(false); } })
      .catch(() => { if (alive) { setHtCatalog([]); setHtFailed(true); } });
    return () => { alive = false; };
  }, [isHoldTime, htCatalog]);
  const [htSel, setHtSel] = useState<Record<string, HoldTimeSel>>({});
  // Enabled ENTRIES (a test with both hold times counts once per enabled type).
  const htEnabled = (htCatalog ?? []).filter((r) => htSel[htKey(r.testMethodId, r.holdTimeType)]?.enabled);
  const [severity, setSeverity] = useState<Severity>('critical');
  const [ack, setAck] = useState(true);
  const [bypass, setBypass] = useState(true);
  const [suppress, setSuppress] = useState(true);
  // Recipients are predefined Recipient GROUPS (not ad-hoc roles) — the user
  // checks one or more live DB groups; the rule links them by their stable ID
  // (never by name, so a later rename keeps the link). Same picker as Rule
  // Detail Edit mode.
  const [groupSel, setGroupSel] = useState<string[]>([]);
  const liveGroups = useNcStore((s) => s.recipients);
  const groupSelRefs = groupRefsFor(groupSel, liveGroups);
  const groupSelNames = groupSelRefs.map((g) => g.name);
  // Email Template — the chosen template's ID (a reference to the existing
  // template, never a copy); null = no template (built-in email). Lives here so it
  // survives Back/Next.
  const [templateId, setTemplateId] = useState<string | null>(null);
  const liveTemplates = useNcStore((s) => s.templates);
  const templateName = templateId ? liveTemplates.find((t) => t.id === templateId)?.name : undefined;

  // Active step list depends on the chosen source. Trigger only for Analysis
  // Result; step numbering follows the active list, so it self-adjusts.
  const hasTrigger = watch === TRIGGER_WATCH_INDEX;
  const stepLabels = hasTrigger ? STEP_LABELS_TRIGGER : STEP_LABELS_PLAIN;
  const stepCount = stepLabels.length;
  // `watch` is only changeable on the Source step (step 1), so `step` can never
  // exceed the active count; clamp defensively anyway.
  const curLabel = stepLabels[Math.min(step, stepCount) - 1] ?? 'Source';

  // Recipients step gate: at least one recipient, each with at least one channel.
  function recipientsError(): string | null {
    if (groupSel.length === 0) return 'Select at least one recipient group.';
    return null;
  }
  // Per-step mandatory validation, evaluated when the user clicks Next. Returns a
  // BO-UI message (shown via notify.warning) or null when the step is valid. The
  // user stays on the current step and keeps every entered value.
  function stepError(): string | null {
    if (curLabel === 'Source' && !name.trim()) return 'Rule Name is required.';
    // Trigger: at least one form/event ON — unless the lab has no enabled workflow
    // form at all, in which case the step itself explains why and can be passed.
    if (curLabel === 'Trigger' && triggerOpts.length > 0 && selectedTriggers.length === 0)
      return 'Select at least one trigger form.';
    if (curLabel === 'Target' && isHoldTime) {
      if (htCatalog === null) return 'Hold-time tests are still loading.';
      if (htFailed) return 'The hold-time tests could not be loaded. Close the wizard and try again.';
      return holdTimeTargetsError(htSel, htCatalog);
    }
    if (curLabel === 'Target' && enabledTargets.length === 0) return 'Select at least one target parameter.';
    if (curLabel === 'Recipients') return recipientsError();
    return null;
  }
  const goNext = () => {
    const err = stepError();
    if (err) { notify.warning(err); return; }
    step < stepCount ? setStep(step + 1) : create();
  };
  function create() {
    const cat = categories.find((c) => c.name === category);
    const seq = (cat?.rules.length ?? 0) + 1;
    // Hold Time: the Test Method targets go in holdTimeTargets; the Permit keys stay
    // empty. Every other category is built exactly as before (no holdTimeTargets).
    const htTargets = isHoldTime ? toSavedTargets(htSel, htCatalog ?? []) : undefined;
    const tgtCount = isHoldTime ? htEnabled.length : enabledTargets.length;
    onCreate(category, {
      name: name.trim() || 'New Rule',
      code: `NEW-${cat?.code ?? 0}-${String(seq).padStart(3, '0')}`,
      purpose: CAT_PURPOSE[category] ?? 'internal',
      severity,
      targets: tgtCount ? `${tgtCount} target${tgtCount === 1 ? '' : 's'}` : undefined,
      // Save the exact ON set so Edit (Rule Detail) reloads it (RTCFR 2026-09-19).
      targetKeys: isHoldTime ? [] : enabledTargets.map((t) => t.key),
      ...(htTargets ? { holdTimeTargets: htTargets } : {}),
      // Triggers are saved ONLY for Analysis Result rules (the ones with the step).
      triggers: hasTrigger && selectedTriggers.length ? selectedTriggers : undefined,
      // Recipients — the selected Recipient GROUPS linked by ID (the server writes
      // the rule↔group junction from these ids); the names are only the display
      // string for the list/KPIs.
      recipients: groupSelNames.join(', '),
      recipientGroups: groupSelRefs,
      // Email Template — linked by ID (AI_NotificationRule.EmailTemplateId); the
      // name in config is only the display snapshot.
      emailTemplateId: templateId,
      config: groupSel.length || templateName
        ? JSON.stringify({ recipientGroups: groupSelNames, ...(templateName ? { emailTemplate: templateName } : {}) })
        : undefined,
      ack,
      enabled: true,
    });
  }

  // Unsaved-changes guard. The wizard is "dirty" once the user has entered a name,
  // moved past step 1, changed the source/category, or picked any target/recipient.
  // Backdrop clicks can't close (Modal disableBackdropClose); the X and Cancel go
  // through here, confirming "Discard unsaved changes?" before closing. The
  // baseline category is the same active-first default the combo initialised to.
  const dirty = step > 1 || name.trim() !== '' || category !== initialCategory || watch !== INITIAL_WATCH_INDEX
    || groupSel.length > 0 || enabledTargets.length > 0 || templateId !== null
    || Object.keys(htSel).length > 0;
  function requestClose() {
    if (!dirty) { onClose(); return; }
    void confirm({
      title: 'Discard unsaved changes?',
      message: 'Your changes to this notification rule will be lost.',
      confirmLabel: 'Discard', cancelLabel: 'Continue Editing', tone: 'danger',
    }).then((ok) => { if (ok) onClose(); });
  }

  // Wider popup (880 vs 660) so the full 6-step stepper shows without clipping;
  // .nc-modal max-width:92vw keeps it responsive + centered.
  return (
    <Modal title="New Notification Rule" onClose={requestClose} width={1040} disableBackdropClose>
      {/* stepper — the ONE shared Dynamic Island (BO UI Standard §5) */}
      <div className="nc-wiz-steps">
        <StepIsland
          steps={stepLabels.map((label, i) => ({
            id: label, label,
            state: step === i + 1 ? 'active' : step > i + 1 ? 'done' : 'todo',
          }))}
          onStepClick={(id) => { const n = stepLabels.indexOf(id) + 1; if (n >= 1 && n <= step) setStep(n); }}
          isClickable={(st) => (stepLabels.indexOf(st.id) + 1) <= step}
        />
      </div>
      {/* .nc-wiz-bd carries a RESPONSIVE min-height (min(320px,46vh)) so sparse
          steps keep a comfortable height while the body stays the sole scroll
          area — the cap never plus the fixed header/stepper/footer exceeds the
          88vh modal, so the footer is never clipped on short viewports. */}
      {/* --fill on the Hold Time Target step: the grid scrolls inside itself (search, column
          headers, pager, stepper and footer stay put) instead of the whole body scrolling. */}
      <div className={`nc-modal-bd nc-wiz-bd${curLabel === 'Target' && isHoldTime ? ' nc-wiz-bd--fill' : ''}`}>
        {/* Source */}
        {curLabel === 'Source' && (
          <>
            <div className="nc-form-group"><label className="nc-flabel" htmlFor="nc-wiz-rule-name">Rule name</label><input id="nc-wiz-rule-name" className="nc-finput" value={name} autoFocus onChange={(e) => setName(e.target.value)} /></div>
            <div className="nc-form-group">
              <label className="nc-flabel">What should this rule watch?</label>
              <div className="nc-optcards">
                {WATCH.map((w, i) => (
                  <div key={w.label} className={`nc-optcard${watch === i ? ' selected' : ''}`} onClick={() => selectSource(i)}>
                    <i className={`ti ${w.icon}`} /><div className="nc-oc-label">{w.label}</div><div className="nc-oc-sub">{w.sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="nc-form-group">
              <label className="nc-flabel">Category</label>
              <select className="nc-fselect" value={category} onChange={(e) => setCategory(e.target.value)}>
                {activeCategories.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </>
        )}

        {/* Trigger (Analysis Result only) — multi-select table; forms from Default Settings */}
        {curLabel === 'Trigger' && (
          <>
            <div className="nc-form-group">
              <label className="nc-flabel">Which forms / events should trigger this rule?</label>
              <div className="nc-form-hint" style={{ margin: '0 0 10px' }}>Only the workflow forms enabled in <strong>Default Settings</strong> are listed. Turn on <strong>one or more</strong> — the rule evaluates when the applicable record reaches <strong>any</strong> enabled event (once per record; duplicates suppressed).</div>
              {triggerOpts.length === 0 ? (
                <div className="nc-form-hint" style={{ color: 'var(--nc-crit)' }}>No workflow forms are enabled in Default Settings. Enable a form there before this rule can trigger.</div>
              ) : (
                <table className="nc-tgt-table">
                  <thead><tr><th>Form Name</th><th style={{ width: 90, textAlign: 'center' }}>Enabled</th></tr></thead>
                  <tbody>
                    {triggerOpts.map((f) => (
                      <tr key={f.id}>
                        <td><div className="nc-tgt-name"><i className={`ti ${f.icon}`} style={{ marginRight: 6, color: 'var(--nc-text-mid)' }} />{f.label}</div><div className="nc-tgt-scope">{f.sub}</div></td>
                        <td style={{ textAlign: 'center' }}>
                          {/* Enabled changes ONLY from the toggle itself — the row is not clickable. */}
                          <span className={`nc-toggle${triggers[f.id] ? ' on' : ''}`} role="button" aria-pressed={!!triggers[f.id]}
                            onClick={() => setTriggers((cur) => ({ ...cur, [f.id]: !cur[f.id] }))} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="nc-form-hint" style={{ marginTop: -4 }}><i className="ti ti-bolt" style={{ color: 'var(--nc-orange)', marginRight: 4 }} />{triggersEvaluationText(selectedTriggers)}</div>
          </>
        )}

        {/* Target */}
        {curLabel === 'Target' && isHoldTime && (
          <div className="nc-form-group nc-ht-fill">
            <label className="nc-flabel">Which tests should this rule monitor?</label>
            <div className="nc-form-hint" style={{ margin: '0 0 10px' }}>Every test with a configured <strong>hold time</strong> (Test Guide). A test with both a <strong>Before Prep</strong> and a <strong>Before Analysis</strong> hold time shows them one below the other — each has its own <strong>Alert</strong> and its own <strong>Warn Before</strong> times (each a number of days, hours or minutes before that hold time runs out). An entry with Alert off never alerts.</div>
            {htFailed
              ? <div className="nc-form-hint" style={{ color: 'var(--nc-crit)' }}>The hold-time tests could not be loaded.</div>
              : (
                <HoldTimeTargetsGrid
                  catalog={htCatalog ?? []}
                  loading={htCatalog === null}
                  sel={htSel}
                  onChange={(id, next) => setHtSel((cur) => ({ ...cur, [id]: next }))}
                />
              )}
          </div>
        )}
        {curLabel === 'Target' && !isHoldTime && (
          <div className="nc-form-group">
            <label className="nc-flabel">Which parameters should this rule monitor?</label>
            <div className="nc-form-hint" style={{ margin: '0 0 10px' }}>Every parameter from the <strong>Permit Library</strong> — the source of truth. Search to narrow the list, expand a row to see its configured limits, and turn each one on/off. The rule fires when <strong>any</strong> enabled target matches a validated result.</div>
            {/* Same grid as Rule Detail → Targets; Enabled flips only from the toggle. */}
            {targetsLoading ? (
              <div className="nc-state-row"><span className="nc-inline-spin" /> Loading Permit Library parameters…</div>
            ) : (
              <PermitTargetsGrid
                targets={permitTargets}
                enabled={tgtEnabled}
                onToggle={(key) => setTgtEnabled((cur) => ({ ...cur, [key]: !(cur[key] ?? false) }))}
                expanded={tgtExpanded}
                onExpand={(key) => setTgtExpanded((cur) => ({ ...cur, [key]: !cur[key] }))}
                showSearch
              />
            )}
          </div>
        )}

        {/* Severity */}
        {curLabel === 'Severity' && (
          <>
            <div className="nc-form-group">
              <label className="nc-flabel">How urgent is this?</label>
              <div className="nc-sevopts">
                {SEV.map((s) => (
                  <div key={s.key} className={`nc-sevopt ${s.key}${severity === s.key ? ' selected' : ''}`} onClick={() => setSeverity(s.key)}>
                    <span className={`dot ${s.key}`} /><div className="nc-sevopt-lbl">{s.label}</div><div className="nc-sevopt-sub">{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="nc-form-group">
              <div className="nc-togrow"><div><div className="nc-togrow-text">Requires acknowledgment</div><div className="nc-togrow-sub">A person must explicitly acknowledge before this clears.</div></div><Toggle on={ack} onClick={() => setAck(!ack)} /></div>
              <div className="nc-togrow"><div><div className="nc-togrow-text">Bypass digest batching</div><div className="nc-togrow-sub">Send immediately even if severity would normally allow batching.</div></div><Toggle on={bypass} onClick={() => setBypass(!bypass)} /></div>
              <div className="nc-togrow"><div><div className="nc-togrow-text">Suppress duplicate firing</div><div className="nc-togrow-sub">Re-occurrence increments a repeat counter instead of a new alert.</div></div><Toggle on={suppress} onClick={() => setSuppress(!suppress)} /></div>
            </div>
          </>
        )}

        {/* Recipients — predefined Recipient GROUPS (same picker as Rule Detail Edit). */}
        {curLabel === 'Recipients' && (
          <div className="nc-form-group">
            <label className="nc-flabel">Who should be notified?</label>
            <div className="nc-form-hint" style={{ margin: '0 0 8px' }}>Check one or more <strong>Recipient Groups</strong>. Each group's roles, users and In-App / Email / SMS channels resolve from its own configuration.</div>
            <RecipientGroupPicker selected={groupSel} onChange={setGroupSel} />
          </div>
        )}

        {/* Email Template — the SAME picker + preview as Rule Detail → Email Template. */}
        {curLabel === 'Email Template' && (
          <>
            <div className="nc-form-hint" style={{ margin: '0 0 10px' }}>Choose the <strong>Email Template</strong> this rule's email is rendered with. The rule links to the existing template — it is not copied, so later edits to the template apply here too.</div>
            <EmailTemplatePicker value={templateId} onChange={setTemplateId} />
          </>
        )}

        {/* Review */}
        {curLabel === 'Review' && (
          <>
            <div className="nc-review">
              <div className="nc-review-row"><span className="nc-review-k">Rule name</span><span className="nc-review-v">{name.trim() || 'New Rule'}</span></div>
              <div className="nc-review-row"><span className="nc-review-k">Category</span><span className="nc-review-v">{category}</span></div>
              {hasTrigger && <div className="nc-review-row"><span className="nc-review-k">Triggers</span><span className="nc-review-v">{triggerLabels(selectedTriggers).join(', ') || '— none selected —'}</span></div>}
              <div className="nc-review-row"><span className="nc-review-k">Severity</span><span className="nc-review-v"><span className={`nc-sev ${severity}`}><span className="dot" />{severity.charAt(0).toUpperCase() + severity.slice(1)}</span></span></div>
              <div className="nc-review-row"><span className="nc-review-k">Acknowledgment</span><span className="nc-review-v">{ack ? 'Required' : 'Not required'}</span></div>
              {isHoldTime
                ? <div className="nc-review-row"><span className="nc-review-k">Targets</span><span className="nc-review-v">{htEnabled.length ? `${htEnabled.length} enabled — ${htEnabled.map((r) => targetLabel(r)).join(', ')}` : '— none enabled —'}</span></div>
                : <div className="nc-review-row"><span className="nc-review-k">Targets</span><span className="nc-review-v">{enabledTargets.length ? `${enabledTargets.length} enabled — ${enabledTargets.map((t) => t.parameter).join(', ')}` : '— none enabled —'}</span></div>}
              <div className="nc-review-row"><span className="nc-review-k">Recipient groups</span><span className="nc-review-v">{groupSel.length ? groupSelNames.join(', ') : '— none selected —'}</span></div>
              <div className="nc-review-row"><span className="nc-review-k">Email template</span><span className="nc-review-v">{templateName ?? 'None — built-in email'}</span></div>
            </div>
            <div className="nc-form-hint">Once saved, this rule activates immediately and begins evaluating against live result data. You can disable it anytime from the Rules list without deleting it.</div>
          </>
        )}
      </div>
      <div className="nc-modal-ft">
        <button className="ca-btn" disabled={step === 1} onClick={() => setStep(step - 1)}>Back</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="ca-btn" onClick={requestClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={goNext}>{step === stepCount ? <><i className="ti ti-check" /> Create Rule</> : 'Next'}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ════════════════════ Category New/Edit (icon picker) ════════════════════ */
const CAT_ICONS = ['ti-droplet-exclamation', 'ti-package-import', 'ti-clipboard-check', 'ti-flask', 'ti-users', 'ti-truck-delivery', 'ti-server-2', 'ti-trending-up', 'ti-alert-triangle', 'ti-bell', 'ti-mail', 'ti-shield-check', 'ti-clock-hour-4', 'ti-chart-line', 'ti-microscope', 'ti-droplet'];

export function CategoryForm({ initial, nextOrder, onClose, onSave }: {
  initial?: NcCategory; nextOrder: number;
  onClose: () => void; onSave: (cat: NcCategory) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [order, setOrder] = useState(initial?.order ?? nextOrder);
  const [icon, setIcon] = useState(initial?.icon ?? 'ti-bell');
  const [active, setActive] = useState(initial?.active ?? true);
  return (
    <Modal title={initial ? 'Edit Rule Category' : 'New Rule Category'} onClose={onClose} width={560}>
      <div className="nc-modal-bd">
        <div className="nc-form-row">
          <div className="nc-form-group"><label className="nc-flabel">Category Name</label><input className="nc-finput" value={name} maxLength={120} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. Result Exceedance" /><div className="nc-form-hint">What people see in the sidebar &amp; dropdowns. Rename anytime — it won't break rules.</div></div>
          <div className="nc-form-group"><label className="nc-flabel">Category Code</label><input className="nc-finput nc-finput-ro" value={initial?.codeLabel ?? ''} readOnly disabled placeholder="Autogenerated" /><div className="nc-form-hint">Assigned automatically on save (RC-01, RC-02 …) and never changes.</div></div>
        </div>
        <div className="nc-form-row">
          <div className="nc-form-group"><label className="nc-flabel">Sort order</label><input className="nc-finput" type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /><div className="nc-form-hint">Lower = higher up. Gaps of 10 let you slot a new one between two existing.</div></div>
          <div className="nc-form-group" />
        </div>
        <div className="nc-form-hint" style={{ margin: '-6px 0 14px' }}>Rules link to the <strong>Category Code</strong>, not the name — so renaming or reordering a category is safe.</div>
        <div className="nc-form-group">
          <label className="nc-flabel">Icon</label>
          <div className="nc-iconpicker">
            {CAT_ICONS.map((ic) => <div key={ic} className={`nc-iconpick${icon === ic ? ' selected' : ''}`} onClick={() => setIcon(ic)}><i className={`ti ${ic}`} /></div>)}
          </div>
        </div>
        <div className="nc-togrow"><div><div className="nc-togrow-text">Active</div><div className="nc-togrow-sub">Inactive categories are hidden from the rule-configuration Category dropdown.</div></div><Toggle on={active} onClick={() => setActive(!active)} /></div>
      </div>
      <div className="nc-modal-ft"><div /><div style={{ display: 'flex', gap: 8 }}>
        <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={!name.trim()} onClick={() => onSave({ code: initial?.code ?? 0, codeLabel: initial?.codeLabel, name: name.trim(), icon, order, active, rules: initial?.rules ?? [] })} title="Save"><span className="tb-i">💾</span> Save</button>
      </div></div>
    </Modal>
  );
}

/* ════════════════════ Recipient Group New/Edit ════════════════════
   Reuses the SAME shared <RecipientsEditor> as Notification Rules → Recipients
   (Users datasource, one row per role with departments/users combined, LIMS
   Administrator, In-App/Email/SMS, search, multi-select, always-visible "Add
   selected", duplicate prevention). The group persists the recipient names as
   its `members` display string and the UNION of the chosen channels as its
   list-badge `channels`. */
export function RecipientForm({ initial, mode = 'edit', onClose, onSave }: {
  initial?: NcRecipientGroup; mode?: 'view' | 'edit'; onClose: () => void; onSave: (group: NcRecipientGroup) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  // Prefer the group's PERSISTED structured members (accurate per-member
  // department + channels). Fall back to reconstructing from the display string
  // + union channels for a legacy display-only group with no `recipients`.
  const initRecips: NcRecipient[] = initial?.recipients?.length
    ? initial.recipients.map((r) => ({ ...r }))
    : initial
      ? initial.members.split(',').map((m) => m.trim()).filter((m) => m && m !== '—').map((nm, i) => ({
          id: `g-${i}-${nm}`, name: nm, kind: 'role' as const,
          inApp: (initial.channels ?? []).includes('IN-APP'),
          email: (initial.channels ?? []).includes('EMAIL'),
          sms: (initial.channels ?? []).includes('SMS'),
        }))
      : [];
  const [recips, setRecips] = useState<NcRecipient[]>(initRecips);
  // Individually selected users (Members → Users tab), by their existing user id.
  const [users, setUsers] = useState<NcGroupUser[]>(() => (initial?.users ?? []).map((u) => ({ ...u })));
  const [memberTab, setMemberTab] = useState<'roles' | 'users'>('roles');
  // View = read-only (selected roles only, no add-grid / Save); Edit = full
  // editor. Clicking the Group Name opens View; Edit opens Edit; Save returns
  // to View (the popup stays open showing the saved group read-only).
  const [curMode, setCurMode] = useState<'view' | 'edit'>(mode);
  const readOnly = curMode === 'view';

  function save() {
    const channels = groupChannels(recips, users);
    onSave({
      name: name.trim(),
      members: groupMembersDisplay(recips, users) || '—',
      channels: channels.length ? channels : (initial?.channels ?? []),
      usedBy: initial?.usedBy ?? 0,
      recipients: recips,   // role / department members → the DB member table
      users,                // individually selected users → stored by user id
    });
    setCurMode('view');   // return to View Mode after a save
  }

  const title = readOnly ? 'Recipient Group' : (initial ? 'Edit Recipient Group' : 'New Recipient Group');
  return (
    <Modal title={title} onClose={onClose} width={1040}>
      <div className="nc-modal-bd">
        <div className="nc-form-group">
          <label className="nc-flabel">Group name</label>
          {readOnly
            ? <div className="nc-finput nc-finput-ro">{name || '—'}</div>
            : <input className="nc-finput" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. DW Compliance Team" />}
        </div>
        <div className="nc-form-group">
          <label className="nc-flabel">Members</label>
          {/* SELECTED MEMBERS — ONE shared grid of everything the group notifies:
              the selected Role / Department rows, then the individually selected
              users, each with its channels and Remove (X). Removing a row here
              unticks it in its Add Recipients tab, because both read the same
              state. It is never hidden or reset by switching tabs. */}
          <div className="nc-sec-cap">Selected members</div>
          <RecipientsEditor recipients={recips} onChange={setRecips} users={users} onUsersChange={setUsers}
            readOnly={readOnly} immediateToggle subrowsOnly
            renderAddSection={(rolesGrid) => (
              // ADD RECIPIENTS — the Roles | Users tabs switch ONLY this grid. Both
              // panels stay mounted (the inactive one is hidden), so a search or a
              // staged channel survives a tab switch. Counts = what is selected.
              <div className="nc-add-recipients">
                <div className="nc-sec-cap">Add recipients</div>
                <div className="nc-tabs nc-member-tabs" role="tablist" aria-label="Add recipients">
                  {([['roles', 'Roles', recips.length], ['users', 'Users', users.length]] as const).map(([key, label, n]) => (
                    <button key={key} type="button" role="tab" id={`nc-member-tab-${key}`} aria-controls={`nc-member-panel-${key}`}
                      aria-selected={memberTab === key} className={`nc-tab${memberTab === key ? ' active' : ''}`}
                      onClick={() => setMemberTab(key)}>
                      <i className={`ti ${key === 'roles' ? 'ti-users-group' : 'ti-user'}`} /> {label}
                      <span className="nc-tab-count">{n}</span>
                    </button>
                  ))}
                </div>
                <div className="nc-member-panel" role="tabpanel" id="nc-member-panel-roles" aria-labelledby="nc-member-tab-roles" hidden={memberTab !== 'roles'}>
                  <div className="nc-form-hint" style={{ margin: '0 0 8px' }}>Pick Role + Department rows from the <strong>Users</strong> data source and choose the In-App / Email / SMS channels for each.</div>
                  {rolesGrid}
                </div>
                <div className="nc-member-panel" role="tabpanel" id="nc-member-panel-users" aria-labelledby="nc-member-tab-users" hidden={memberTab !== 'users'}>
                  <div className="nc-form-hint" style={{ margin: '0 0 8px' }}>Add <strong>individual users</strong> to this group and choose their In App / Email / SMS channels. A user who is also reached through a selected role is notified only once.</div>
                  <GroupUsersEditor users={users} onChange={setUsers} />
                </div>
              </div>
            )} />
        </div>
      </div>
      <div className="nc-modal-ft"><div /><div style={{ display: 'flex', gap: 8 }}>
        {readOnly ? (
          <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Close</button>
        ) : (
          <>
            <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            <button className="ca-btn ca-btn-solid" disabled={!name.trim() || (recips.length === 0 && users.length === 0)} onClick={save} title="Save"><span className="tb-i">💾</span> Save</button>
          </>
        )}
      </div></div>
    </Modal>
  );
}

/* ════════════════════ Email Template New/Edit (subject + rich body + fields) ════════════════════ */
// The Insert Field panel is DATABASE-DRIVEN (RTCFR 2026-10-19): it lists the ACTIVE fields
// mapped to the template's category in dbo.AI_NotificationTemplateField, in Display Order —
// never a hardcoded list. Administrators add / edit / retire fields through Manage Fields;
// a field is offered only for the category it is mapped to. Every Alert field's code is one
// the API's composers fill (NotificationAlertFieldCatalog — unit-tested), and a Database
// field is read from its approved Source Table + Column for the alert's own sample tests.

/** How long the editor waits after the last keystroke before re-checking its placeholders. */
const FIELD_CHECK_DEBOUNCE_MS = 600;

/** Seed bodies are plain text with newlines; the rich editor renders HTML, so a
 *  plain body is escaped and its line breaks turned into <br> once on load. An
 *  already-HTML body is passed through untouched (tokens survive either way). */
function bodyToHtml(raw: string): string {
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  const esc = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(/\r?\n/g, '<br>');
}
/** True when the editor HTML carries no visible text (an untouched editor still
 *  holds "<p><br></p>") — so an empty body saves as undefined, as before. */
function isBlankHtml(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

export function TemplateForm({ initial, categories, onClose, onSave }: {
  initial?: NcTemplate; categories: FieldCategory[]; onClose: () => void; onSave: (tpl: NcTemplate) => void;
}) {
  const categoryNames = categories.map((c) => c.name);
  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState(initial?.category ?? categoryNames[0] ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [body, setBody] = useState(initial?.body ? bodyToHtml(initial.body) : '');
  const [active, setActive] = useState(initial?.active ?? true);
  const { confirm } = useConfirm();
  const subjectRef = useRef<HTMLInputElement>(null);
  // Which field a token click targets — the LAST one the user focused. The body
  // wins by default so the very first click still lands somewhere sensible.
  const activeField = useRef<'subject' | 'body'>('body');

  // The category's fields, from the database; reloaded on a category change and after Manage Fields.
  const categoryCode = categories.find((c) => c.name === category)?.code;
  const [fields, setFields] = useState<TemplateField[]>([]);
  const [fieldsState, setFieldsState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [fieldsVersion, setFieldsVersion] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);
  const [warnings, setWarnings] = useState<TemplateFieldWarning[]>([]);
  useEffect(() => {
    if (!categoryCode) { setFields([]); setFieldsState('ready'); return; }
    let live = true;
    setFieldsState('loading');
    listTemplateFields(categoryCode)
      .then((f) => { if (live) { setFields(f); setFieldsState('ready'); } })
      .catch(() => { if (live) { setFields([]); setFieldsState('error'); } });
    return () => { live = false; };
  }, [categoryCode, fieldsVersion]);

  // Placeholder warnings (inactive / not a field of this category / source unreadable), checked
  // by the server a moment after the user stops typing.
  useEffect(() => {
    if (!categoryCode || !`${subject}${body}`.includes('{{')) { setWarnings([]); return; }
    let live = true;
    const t = window.setTimeout(() => {
      checkTemplateFields(categoryCode, subject, body)
        .then((w) => { if (live) setWarnings(w); })
        .catch(() => { if (live) setWarnings([]); });
    }, FIELD_CHECK_DEBOUNCE_MS);
    return () => { live = false; window.clearTimeout(t); };
  }, [categoryCode, subject, body, fieldsVersion]);

  // Backdrop clicks can't close the popup (Modal disableBackdropClose); the X and
  // Cancel route here, confirming "Discard unsaved changes?" only when the user
  // has actually entered/edited something — so no data is lost by a stray click.
  const initialBody = initial?.body ? bodyToHtml(initial.body) : '';
  const dirty = name.trim() !== (initial?.name ?? '') || category !== (initial?.category ?? categoryNames[0] ?? '')
    || subject !== (initial?.subject ?? '') || body !== initialBody || active !== (initial?.active ?? true);
  function requestClose() {
    if (!dirty) { onClose(); return; }
    void confirm({
      title: 'Discard unsaved changes?',
      message: 'Your changes to this email template will be lost.',
      confirmLabel: 'Discard', cancelLabel: 'Continue Editing', tone: 'danger',
    }).then((ok) => { if (ok) onClose(); });
  }

  // Insert the clicked token at the caret of the ACTIVE field. The token button
  // uses onMouseDown + preventDefault so focus (and the caret/selection) stays in
  // that field, so the insert targets exactly where the user was typing.
  function insertToken(tok: string) {
    if (activeField.current === 'subject') {
      const el = subjectRef.current;
      if (!el) { setSubject((s) => s + tok); return; }
      const start = el.selectionStart ?? subject.length, end = el.selectionEnd ?? subject.length;
      setSubject((s) => s.slice(0, start) + tok + s.slice(end));
      requestAnimationFrame(() => { el.focus(); const p = start + tok.length; el.setSelectionRange(p, p); });
    } else {
      // Body — insert literal token text at the caret of the focused
      // contentEditable; the RichTextEditor's onInput propagates it to `body`.
      document.execCommand('insertText', false, tok);
    }
  }

  return (
    <Modal title={initial ? 'Edit Email Template' : 'New Email Template'} onClose={requestClose} width={820} disableBackdropClose>
      <div className="nc-modal-bd">
        <div className="nc-form-row">
          <div className="nc-form-group"><label className="nc-flabel">Template name</label><input className="nc-finput" value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="e.g. Critical Exceedance" /></div>
          <div className="nc-form-group"><label className="nc-flabel">Category</label><select className="nc-fselect" value={category} onChange={(e) => setCategory(e.target.value)}>{categoryNames.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        </div>
        <div className="nc-form-group"><label className="nc-flabel">Subject</label><input ref={subjectRef} className="nc-finput" value={subject} onFocus={() => { activeField.current = 'subject'; }} onChange={(e) => setSubject(e.target.value)} placeholder="[ALPACA] {{RuleName}} — {{SampleId}}" /></div>
        <div className="nc-form-group">
          <label className="nc-flabel">Body</label>
          <div className="nc-tpl-editrow">
            {/* Rich-text body — the shared RichTextEditor with the full Word-style
                toolbar (font, numeric size, grow/shrink, case, B/I/U/strike,
                sub/sup, highlight + font colour swatches, lists, indent, align +
                justify, line spacing, clear formatting, undo/redo). */}
            <div className="nc-tpl-body" onFocusCapture={() => { activeField.current = 'body'; }}>
              <RichTextEditor value={body} onChange={setBody} variant="full" wordToolbar
                renderPreview={() => <TemplatePreviewPane categoryCode={categoryCode} subject={subject} body={body} />} />
            </div>
            {/* Field panel — always visible beside the editor; a click inserts the
                field token into whichever field (Subject / Body) was last focused.
                Only the category's ACTIVE fields are listed, so an inactive field
                cannot be newly inserted. */}
            <div className="nc-tpl-tokens">
              <div className="nc-tpl-tokens-top">
                <div className="nc-tpl-tokens-hd">Insert Field</div>
                <button type="button" className="nc-tpl-manage" onClick={() => setManageOpen(true)}
                  title="Add, edit or retire the Insert Fields of each category">
                  <i className="ti ti-settings" /> Manage Fields
                </button>
              </div>
              {fieldsState === 'loading' && <div className="nc-tpl-target">Loading fields…</div>}
              {fieldsState === 'error' && (
                <div className="nc-tpl-target">Could not load the fields.{' '}
                  <button type="button" className="nc-tpl-link" onClick={() => setFieldsVersion((v) => v + 1)}>Retry</button>
                </div>
              )}
              {fieldsState === 'ready' && fields.length === 0 && (
                <div className="nc-tpl-target">No fields are configured for this category. Use Manage Fields to add one.</div>
              )}
              {fieldsState === 'ready' && fields.map((f) => (
                <button key={f.id} type="button" className="nc-tpl-tok"
                  title={`${f.fieldName} — ${f.sourceTable ? `${f.sourceTable}.${f.sourceColumn}` : 'computed by the alert'}`}
                  onMouseDown={(e) => { e.preventDefault(); insertToken(f.fieldCode); }}>{f.fieldCode}</button>
              ))}
              <div className="nc-tpl-target">Inserts into the Subject or Body — whichever you last clicked into.</div>
            </div>
          </div>
          {warnings.length > 0 && (
            <div className="nc-tpl-warns" role="status" aria-label="Field warnings">
              {warnings.map((w) => (
                <div key={`${w.fieldCode}|${w.kind}`} className={`nc-tpl-warn nc-tpl-warn-${w.kind.toLowerCase()}`}>
                  <i className="ti ti-alert-triangle" /> {w.message}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="nc-togrow"><div><div className="nc-togrow-text">Active</div><div className="nc-togrow-sub">Inactive templates fall back to the category default.</div></div><Toggle on={active} onClick={() => setActive(!active)} /></div>
      </div>
      <div className="nc-modal-ft"><div /><div style={{ display: 'flex', gap: 8 }}>
        <button className="ca-btn" onClick={requestClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        <button className="ca-btn ca-btn-solid" disabled={!name.trim() || !subject.trim()} onClick={() => onSave({ name: name.trim(), category, subject: subject.trim(), body: isBlankHtml(body) ? undefined : body, active })} title="Save"><span className="tb-i">💾</span> Save</button>
      </div></div>
      {manageOpen && (
        <TemplateFieldsManager categories={categories} initialCategoryCode={categoryCode}
          onClose={() => setManageOpen(false)} onChanged={() => setFieldsVersion((v) => v + 1)} />
      )}
    </Modal>
  );
}

/** The body editor's Preview tab: the subject + body rendered by the SERVER with one real job's
 *  values (the same renderer the alerts use), so what it shows is what an alert would send.
 *  Values only an alert can compute ({{Limit}}, {{Count}} …) show as ‹Field Name›. */
function TemplatePreviewPane({ categoryCode, subject, body }: { categoryCode?: number; subject: string; body: string }) {
  const [jobInput, setJobInput] = useState('');
  const [jobId, setJobId] = useState('');
  // Bumped by Refresh, so it re-requests even when the Job ID is unchanged (e.g. after a failure).
  const [nonce, setNonce] = useState(0);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!categoryCode) { setPreview(null); setError('Choose a category to preview this template.'); return; }
    let live = true;
    setBusy(true);
    setError(null);
    previewTemplate(categoryCode, subject, body, jobId)
      .then((p) => { if (live) setPreview(p); })
      .catch((e) => { if (live) { setPreview(null); setError(templateFieldRefusal(e) ?? 'The preview could not be generated.'); } })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [categoryCode, subject, body, jobId, nonce]);

  const refresh = () => { setJobId(jobInput.trim()); setNonce((n) => n + 1); };

  return (
    <div className="nc-tpl-pv">
      <div className="nc-tpl-pv-bar">
        <label className="nc-tpl-pv-job">
          <span>Preview with Job ID</span>
          <input className="nc-finput" value={jobInput} placeholder="Latest job" aria-label="Preview Job ID" data-unsaved-ignore
            onChange={(e) => setJobInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); refresh(); } }} />
        </label>
        <button type="button" className="ca-btn" onClick={refresh}><i className="ti ti-refresh" /> Refresh</button>
        {preview?.jobId && <span className="nc-tpl-pv-ctx">Job {preview.jobId} · Sample {preview.sampleId}</span>}
      </div>
      {busy && <div className="nc-tpl-target">Generating preview…</div>}
      {error && <div className="nc-tpl-warn">{error}</div>}
      {preview && !busy && (
        <>
          {!preview.jobId && <div className="nc-tpl-warn">No job with samples was found{jobId ? ` for Job ID ${jobId}` : ''} — field values show as ‹Field Name›.</div>}
          <div className="nc-tpl-pv-subj"><span>Subject:</span> {preview.subject}</div>
          <div className="nc-tpl-pv-body" dangerouslySetInnerHTML={{ __html: preview.htmlBody }} />
          {preview.warnings.map((w) => (
            <div key={`${w.fieldCode}|${w.kind}`} className="nc-tpl-warn"><i className="ti ti-alert-triangle" /> {w.message}</div>
          ))}
        </>
      )}
    </div>
  );
}
